// Zustand einer Aufnahme auf dem Gerät des Kunden ("Draft").
//
// Während der Erfassung ist der lokale Zustand maßgeblich: Jede Änderung
// landet sofort in IndexedDB (speicher.ts) und als Auftrag in der
// Warteschlange (sync.ts). Der Server ist erst beim Öffnen ohne offene
// Aufträge und nach dem Absenden die Quelle.
//
// Alle Funktionen hier sind rein (kein DOM, kein I/O) und getestet.

import {
  ROOM_TYPE_LABEL,
  ROOM_TYPES,
  SIDE_ROOM_TYPES,
  type DrivewayType,
  type ElevatorSize,
  type ParkingOption,
  type StairwellType,
  type StepsToDoor,
  type CheckDraft,
  type MarkAction,
  type PhotoStep,
  type HallSection,
  type HallShape,
  type RoomPhotoMode,
  type RoomShape,
  type RoomDoor,
  type LampsInfo,
  type RoomWall,
  type RoomType,
  type Service,
  type SpecialItem,
  type KitchenInfo,
  type HallSide,
  type SurveyStatus,
} from "@umzugsscan/shared";

export type Branding = {
  name: string;
  slug: string | null;
  /** Kunden-Version: Umzugsscan statt einer Firma. */
  privat?: boolean;
  phone: string | null;
  email: string | null;
  website: string | null;
  logoUrl: string | null;
  /** Löschfrist der Fotos in Tagen (Hinweis vor dem ersten Foto). */
  photoRetentionDays?: number;
  /** Demo-Weg ohne Anmeldung (12.10.2026): Beispielfotos, am Ende der Demo-Bericht. */
  demo?: boolean;
};

/** Kunden-Version: Ergebnis ohne Firmenwerte (server/src/bericht/kunde.ts). */
export type PrivatResult = {
  rooms: { name: string; type: string; items: { name: string; qty: number; disposal: boolean }[] }[];
  itemCount: number;
  boxesRough: number;
  /** Umzugsvolumen als Spanne ohne Einheit (12.10.2026); fehlt bei älteren Ständen im Gerätespeicher. */
  volumeText?: string;
  fromCity: string | null;
  toCity: string | null;
  dateText: string;
  fileName: string;
};
export type PrivatInfo = {
  code: string | null;
  result: PrivatResult | null;
  /** Firmen, die übernehmen wollen oder übernommen haben (Block 3). */
  transfers?: { companyName: string; status: "angefragt" | "bestaetigt" | "abgelehnt"; createdAt: string }[];
  /** Bezahlen nach der Erfassung, vor der Auswertung (08.10.2026). */
  payment?: { priceCents: number; required: boolean; paid: boolean; started: boolean; playBilling?: boolean; playProductId?: string | null };
};

/** Demo-Weg: Eckdaten des Berichts, sobald ausgewertet; tagesgrenze = heute keine Demo-Auswertung mehr. */
export type DemoInfo = {
  tagesgrenze: boolean;
  result: { volumeText: string; totalM3: number; boxes: number; weightKg: number; manHours: number; rooms: number; items: number; hints: string[] } | null;
};

export type Address = {
  street: string | null;
  zip: string | null;
  city: string | null;
  floor: number | null;
  elevator: boolean | null;
  walkDistanceM: number | null;
  // Zugang und Parken, optional
  stairwell: StairwellType | null;
  elevatorSize: ElevatorSize | null;
  stepsToDoor: StepsToDoor | null;
  parking: ParkingOption | null;
  driveway: DrivewayType | null;
  note: string | null;
  /** Neue Adresse steht noch nicht fest (30.10.2026): PLZ und Ort genügen. */
  pending: boolean;
};

export type Room = {
  id: string;
  type: RoomType;
  name: string;
  sort: number;
  confirmedComplete: boolean;
  /** Antwort auf "Gibt es hier große Schränke?" (nur in CABINET_ROOM_TYPES gefragt). */
  cabinets?: "ja" | "nein" | null;
  // Grundriss (08.10.2026): Etage, Flur und Tür, über die der Raum erreicht wird.
  floor?: number;
  hallRoomId?: string | null;
  hallSide?: HallSide | null;
  hallOrder?: number | null;
  doorPhotoId?: string | null;
  doorX?: number | null;
  doorY?: number | null;
  hallLayout?: string | null;
  /** Flur als Weg aus Abschnitten (14.10.2026): Form und Abschnitte am Flur, Abschnitt der Tür am Raum. */
  hallShape?: HallShape | null;
  hallSections?: HallSection[] | null;
  hallSection?: number | null;
  /** Lage der Tür an ihrer Flurwand, 0..1 (30.10.2026, in der Skizze gezogen). */
  hallPos?: number | null;
  /** Raum nach Wänden (15.10.2026): Aufnahmeart (waende, kurz) und Form; null = ältere Aufnahme mit Rundum-Fotos. */
  photoMode?: RoomPhotoMode | null;
  roomShape?: RoomShape | null;
  /** Türen je Raum (15.10.2026): Wand des Elternraums mit der Tür hierher; Türen dieses Raums mit Entscheidung. */
  doorWall?: RoomWall | null;
  doors?: RoomDoor[] | null;
};

export type Mark = { x: number; y: number; action: MarkAction; label: string | null };

export type Photo = {
  id: string;
  roomId: string;
  step: PhotoStep;
  /** Abschnitt des Flurs (nur Flurfotos, 14.10.2026). */
  section?: number | null;
  blurScore: number | null;
  brightness: number | null;
  marks: Mark[];
  /** true, sobald der Server das Foto bestätigt hat. */
  uploaded: boolean;
  /** Server-URL (nur für Fotos, die nicht mehr lokal liegen, z. B. anderes Gerät). */
  url: string | null;
  createdAt: string;
};

export type SurveyFields = {
  contactName: string | null;
  phone: string | null;
  email: string | null;
  dateFixed: boolean | null;
  dateFrom: string | null;
  dateTo: string | null;
  services: Partial<Record<Service, boolean>>;
  customerNote: string | null;
  /** Haushalt (optional): Wohnfläche des Auszugs und Personenzahl, Grundlage der Gegenprobe im Bericht. */
  livingAreaM2: number | null;
  persons: number | null;
  /** Die Firma hat um weitere Fotos gebeten (Nachricht), sonst null. Nur vom Server, nie zurückgeschickt. */
  reopenNote: string | null;
  consentPrivacy: boolean;
  consentTraining: boolean;
  /** Hinweis vor dem ersten Foto bestätigt (photoNoticeText in @umzugsscan/shared). */
  photoNotice: boolean;
  /** Sonderteile und Zerbrechliches (eigene Zwischenschritte). */
  specialItems: SpecialItem[];
  /** Angaben zur Küche (eigener Schritt). */
  kitchen: KitchenInfo;
  /** Lampen ab- und anbauen (15.10.2026). */
  lamps?: LampsInfo;
};

export const SCREENS = [
  "willkommen",
  "kontakt",
  "termin",
  "auszug",
  "einzug",
  "eingang",
  "grundriss",
  "raeume",
  "fotos",
  "besonderes",
  "markieren",
  "sonderteile",
  "zerbrechliches",
  "kueche",
  "lampen",
  "leistungen",
  "pruefung",
  "danke",
] as const;
export type Screen = (typeof SCREENS)[number];

export type Phase = "rundum" | "tueren" | "schrank";

export type Nav = {
  screen: Screen;
  /** Index in guideSteps() auf dem Foto-Bildschirm. */
  guide: number;
  phase: Phase;
  /** Kam der Kunde über "Beheben" aus der Prüfung? Dann führt "Weiter" zurück. */
  returnToCheck: boolean;
};

export type Draft = {
  token: string;
  resumeUrl: string;
  company: Branding;
  status: SurveyStatus;
  /** Nur Kunden-Version. */
  privat?: PrivatInfo | null;
  /** Nur Demo-Weg. */
  demo?: DemoInfo | null;
  survey: SurveyFields;
  addresses: { from: Address; to: Address };
  rooms: Room[];
  photos: Photo[];
  nav: Nav;
};

export const EMPTY_ADDRESS: Address = {
  street: null,
  zip: null,
  city: null,
  floor: null,
  elevator: null,
  walkDistanceM: null,
  stairwell: null,
  elevatorSize: null,
  stepsToDoor: null,
  parking: null,
  driveway: null,
  note: null,
  pending: false,
};

export const START_NAV: Nav = { screen: "willkommen", guide: 0, phase: "rundum", returnToCheck: false };

// ------------------------------------------------------------- Server → Draft

/** Antwort von GET /erfassung/:token (Ausschnitt, den die Erfassung nutzt). */
export type ServerState = {
  token: string;
  resumeUrl: string;
  status: SurveyStatus;
  company: Branding;
  privat?: PrivatInfo | null;
  demo?: DemoInfo | null;
  survey: SurveyFields & { progress: Record<string, unknown>; submittedAt: string | null };
  addresses: (Address & { kind: "from" | "to" })[];
  rooms: Room[];
  photos: {
    id: string;
    roomId: string;
    step: PhotoStep;
    hallSection?: number | null;
    blurScore: number | null;
    brightness: number | null;
    createdAt: string;
    url: string | null;
    marks: { x: number; y: number; action: MarkAction; label: string | null }[];
  }[];
};

function navFromProgress(progress: Record<string, unknown> | undefined): Nav | null {
  if (!progress) return null;
  const screen = progress.screen;
  if (typeof screen !== "string" || !(SCREENS as readonly string[]).includes(screen)) return null;
  return {
    screen: screen as Screen,
    guide: typeof progress.guide === "number" ? progress.guide : 0,
    phase: progress.phase === "schrank" ? "schrank" : progress.phase === "tueren" ? "tueren" : "rundum",
    returnToCheck: false,
  };
}

export function fromServer(s: ServerState, prev?: Draft | null): Draft {
  const addr = (kind: "from" | "to"): Address => {
    const a = s.addresses.find((x) => x.kind === kind);
    if (!a) return { ...EMPTY_ADDRESS };
    return {
      street: a.street,
      zip: a.zip,
      city: a.city,
      floor: a.floor,
      elevator: a.elevator,
      walkDistanceM: a.walkDistanceM,
      stairwell: a.stairwell ?? null,
      elevatorSize: a.elevatorSize ?? null,
      stepsToDoor: a.stepsToDoor ?? null,
      parking: a.parking ?? null,
      driveway: a.driveway ?? null,
      note: a.note ?? null,
      pending: a.pending ?? false,
    };
  };
  const { progress, submittedAt: _, ...survey } = s.survey;
  let nav = prev?.nav ?? navFromProgress(progress) ?? START_NAV;
  if (s.status !== "entwurf") nav = { ...START_NAV, screen: "danke" };
  // Die Firma hat um weitere Fotos gebeten: statt "Danke" oder dem Anfang auf die Prüfseite (mit der Bitte oben).
  else if (survey.reopenNote && (nav.screen === "danke" || nav.screen === "willkommen")) nav = { ...START_NAV, screen: "pruefung" };
  return {
    token: s.token,
    resumeUrl: s.resumeUrl,
    company: s.company,
    status: s.status,
    privat: s.privat ?? null,
    demo: s.demo ?? null,
    survey: { ...survey, services: survey.services ?? {}, photoNotice: survey.photoNotice ?? false, specialItems: survey.specialItems ?? [], kitchen: survey.kitchen ?? {}, lamps: survey.lamps ?? { wanted: null, items: [], neu: {} } },
    addresses: { from: addr("from"), to: addr("to") },
    rooms: [...s.rooms].sort((a, b) => a.sort - b.sort),
    photos: s.photos.map((p) => ({
      id: p.id,
      roomId: p.roomId,
      step: p.step,
      section: p.hallSection ?? null,
      blurScore: p.blurScore,
      brightness: p.brightness,
      marks: p.marks.map((m) => ({ x: m.x, y: m.y, action: m.action, label: m.label ?? null })),
      uploaded: true,
      url: p.url,
      createdAt: typeof p.createdAt === "string" ? p.createdAt : new Date(p.createdAt).toISOString(),
    })),
    nav,
  };
}

// ------------------------------------------------------------ Änderungen

export function patchSurvey(d: Draft, patch: Partial<SurveyFields>): Draft {
  const services = patch.services ? { ...d.survey.services, ...patch.services } : d.survey.services;
  return { ...d, survey: { ...d.survey, ...patch, services } };
}

export function patchAddress(d: Draft, kind: "from" | "to", patch: Partial<Address>): Draft {
  return { ...d, addresses: { ...d.addresses, [kind]: { ...d.addresses[kind], ...patch } } };
}

export function upsertRoom(d: Draft, room: Room): Draft {
  const rooms = d.rooms.some((r) => r.id === room.id)
    ? d.rooms.map((r) => (r.id === room.id ? room : r))
    : [...d.rooms, room];
  return { ...d, rooms: rooms.sort((a, b) => a.sort - b.sort) };
}

export function removeRoom(d: Draft, id: string): Draft {
  return { ...d, rooms: d.rooms.filter((r) => r.id !== id), photos: d.photos.filter((p) => p.roomId !== id) };
}

export function addPhoto(d: Draft, photo: Photo): Draft {
  return { ...d, photos: [...d.photos.filter((p) => p.id !== photo.id), photo] };
}

export function removePhoto(d: Draft, id: string): Draft {
  return { ...d, photos: d.photos.filter((p) => p.id !== id) };
}

export function markUploaded(d: Draft, id: string): Draft {
  if (!d.photos.some((p) => p.id === id && !p.uploaded)) return d;
  return { ...d, photos: d.photos.map((p) => (p.id === id ? { ...p, uploaded: true } : p)) };
}

export function setMarks(d: Draft, id: string, marks: Mark[]): Draft {
  return { ...d, photos: d.photos.map((p) => (p.id === id ? { ...p, marks } : p)) };
}

export function setNav(d: Draft, nav: Partial<Nav>): Draft {
  return { ...d, nav: { ...d.nav, ...nav } };
}

// ------------------------------------------------------------ Räume zählen

/** Reihenfolge der Raumtypen in der Aufnahme: Flur zuerst, Nebenräume zuletzt. */
const TYPE_ORDER: RoomType[] = [
  "flur",
  ...ROOM_TYPES.filter((t) => t !== "flur" && !SIDE_ROOM_TYPES.includes(t)),
  ...SIDE_ROOM_TYPES,
];

export const DEFAULT_COUNTS: Partial<Record<RoomType, number>> = {
  flur: 1,
  wohnen: 1,
  schlafen: 1,
  kueche: 1,
  bad: 1,
};

export function roomCounts(rooms: Room[]): Partial<Record<RoomType, number>> {
  const c: Partial<Record<RoomType, number>> = {};
  for (const r of rooms) c[r.type] = (c[r.type] ?? 0) + 1;
  return c;
}

export function roomName(type: RoomType, n: number): string {
  // Kurze Namen für die Anzeige; "Flur / Diele" usw. nur in der Auswahl.
  const base = ROOM_TYPE_LABEL[type].split(" / ")[0]!;
  return n === 1 ? base : `${base} ${n}`;
}

/**
 * Gleicht die Räume an die gewählten Anzahlen an. Bestehende Räume (mit
 * ihren Fotos) bleiben; neue kommen hinten dazu, überzählige werden von
 * hinten entfernt.
 */
export function reconcileRooms(
  rooms: Room[],
  counts: Partial<Record<RoomType, number>>,
  newId: () => string,
): { add: Room[]; remove: Room[] } {
  const add: Room[] = [];
  const remove: Room[] = [];
  for (const type of TYPE_ORDER) {
    const existing = rooms.filter((r) => r.type === type).sort((a, b) => a.sort - b.sort);
    const want = Math.max(0, counts[type] ?? 0);
    for (let n = existing.length + 1; n <= want; n++) {
      add.push({ id: newId(), type, name: roomName(type, n), sort: TYPE_ORDER.indexOf(type) * 10 + n, confirmedComplete: false });
    }
    if (existing.length > want) remove.push(...existing.slice(want));
  }
  return { add, remove };
}

// ------------------------------------------------------------ Prüfung

export function toCheckDraft(d: Draft): CheckDraft {
  return {
    contactName: d.survey.contactName,
    phone: d.survey.phone,
    email: d.survey.email,
    dateFixed: d.survey.dateFixed,
    dateFrom: d.survey.dateFrom,
    dateTo: d.survey.dateTo,
    consentPrivacy: d.survey.consentPrivacy,
    addresses: [
      { kind: "from", ...d.addresses.from },
      { kind: "to", ...d.addresses.to },
    ],
    rooms: d.rooms,
    photos: d.photos.map((p) => ({
      id: p.id,
      roomId: p.roomId,
      step: p.step,
      blurScore: p.blurScore,
      brightness: p.brightness,
    })),
  };
}
